import { createApi } from "@reduxjs/toolkit/query/react";

import { triggerFileDownload } from "@/shared/helpers/trigger-file-download.helper";
import { type IApiResponse } from "@/shared/interfaces/api-response.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";
import districtsApi from "../districts/districts";

import {
  type IUpdateDocumentNameRequest,
  type IUserDocumentsListResponse,
  type IGetDocumentByFileResponse,
  type IExportDocumentWithCoordinatesSuccessResult,
} from "./documents.interface";
import { fetchDocumentWithCoordinates } from "./functions";

const documentsApi = createApi({
  reducerPath: "documentsApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.Documents],
  endpoints: (builder) => ({
    getUserDocuments: builder.query<
      IUserDocumentsListResponse,
      { districtId: string | null; page?: number; limit?: number }
    >({
      query: ({ districtId, page = 1, limit = 20 }) => ({
        url: "/documents/user/list",
        method: "GET",
        params: { districtId, page, limit },
      }),
      providesTags: [ApiTags.Documents],
      transformResponse: (result: IApiResponse<IUserDocumentsListResponse>) =>
        result.data,
    }),
    updateDocumentName: builder.mutation<void, { id: string; name: string }>({
      query: ({ id, name }) => ({
        url: `/documents/${id}`,
        method: "PATCH",
        body: { name } as IUpdateDocumentNameRequest,
      }),
      invalidatesTags: [ApiTags.Documents],
    }),
    deleteDocument: builder.mutation<void, string>({
      query: (id) => ({
        url: `/documents/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [ApiTags.Documents],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(districtsApi.util.invalidateTags([ApiTags.Districts]));
        } catch (error) {
          console.error("Failed to delete document:", error);
        }
      },
    }),
    getDocumentByFile: builder.mutation<IGetDocumentByFileResponse, string>({
      query: (fileId) => ({
        url: `/documents/by-file/${fileId}`,
        method: "GET",
      }),
      transformResponse: (
        result: IApiResponse<IGetDocumentByFileResponse>,
      ): IGetDocumentByFileResponse => result.data,
    }),
    exportDocumentWithCoordinates: builder.mutation<
      IExportDocumentWithCoordinatesSuccessResult,
      { documentId: string; fallbackFilename: string }
    >({
      queryFn: async ({ documentId, fallbackFilename }) => {
        const result = await fetchDocumentWithCoordinates(
          documentId,
          fallbackFilename,
        );
        if (result.error) {
          return {
            error: { status: result.error.statusCode, data: result.error },
          };
        }
        if (!result.data) {
          return {
            error: {
              status: 0,
              data: { message: "Export failed" },
            },
          };
        }
        triggerFileDownload(result.data.blob, result.data.filename);
        return {
          data: { success: true as const, filename: result.data.filename },
        };
      },
    }),
  }),
});

export const {
  useGetUserDocumentsQuery,
  useUpdateDocumentNameMutation,
  useDeleteDocumentMutation,
  useGetDocumentByFileMutation,
  useExportDocumentWithCoordinatesMutation,
} = documentsApi;
export default documentsApi;
